import React, { FC, useState } from 'react';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';
import cx from 'classnames';
import { API } from 'core/API';
import { SUPER_ADMIN_RESET_PASSWORD_FOR_MEMBER } from 'core/passwordRecovery';
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import InputText from 'shared/components/FormInputs/InputText';
import InputErrorHint from 'shared/components/InputErrorHint';
import InputContainer from 'shared/components/FormInputs/InputContainer';
import Loader from 'shared/components/Loader';
import styles from './RecoverySendEmailForm.module.scss';

const initialValues = {
	email: '',
};

const validationSchema = Yup.object().shape({
	email: Yup.string().email('Invalid email').required('Email is required'),
});

type RecoverySendEmailFormProps = {
	setSentEmailSuccess: React.Dispatch<React.SetStateAction<boolean>>;
	setResponseError: React.Dispatch<React.SetStateAction<boolean>>;
	setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
};

const RecoverySendEmailForm: FC<RecoverySendEmailFormProps> = ({
	setSentEmailSuccess,
	setResponseError,
	setErrorMessage,
}) => {
	const [isLoading, setIsLoading] = useState(false);

	const submitClickHandler = async (values: { email: string }) => {
		try {
			setIsLoading(true);
			await API.resetPassword(values.email, undefined, true);
			setSentEmailSuccess(true);
		} catch (error: any) {
			const isUserNotFound =
				error?.response?.code === 404 ||
				error?.message === 'USER_NOT_FOUND' ||
				error?.response?.message === 'USER_NOT_FOUND';

			if (isUserNotFound) {
				setResponseError(true);
				setErrorMessage('No account found for this email address.');
			} else if (error?.message === 'ERROR_RESET_PASSWORD' || error?.response?.message) {
				setResponseError(true);
				setErrorMessage(error?.response?.message || error.message);
			} else {
				setResponseError(true);
				setErrorMessage(error?.message || 'Something went wrong. Please try again.');
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className={styles.container}>
			<h1 className={cx('title', styles.title)}>Forgot password</h1>
			<span className={styles.subTitle}>
				Please enter your e-mail and we’ll send instruction how to change password
			</span>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={values => submitClickHandler(values)}
			>
				{({ errors, touched, setFieldValue }) => (
					<Form className={styles.form}>
						<Field name="email">
							{({ field }: FieldProps) => (
								<InputContainer isDisabledMargin>
									<InputText field={field} placeholder="Email" setFieldValue={setFieldValue} />
									{errors.email && touched.email && <InputErrorHint errorText={errors.email} />}
								</InputContainer>
							)}
						</Field>
						{isLoading ? (
							<Loader />
						) : (
							<ButtonDefault submitType classList={['primary', 'extra_lg']} title="Send email" />
						)}
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default RecoverySendEmailForm;
